import React, { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconEdit,
  IconLink,
  IconSearch,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import { useGetTherapeuticIndicationsForAdmin } from '../../../global/entities/TherapeuticIndication/queries/useGetTherapeuticIndicationsForAdmin.tsx';
import { useUpdateTherapeuticIndicationResourceUrl } from '../../../global/entities/TherapeuticIndication/mutations/useUpdateTherapeuticIndicationResourceUrl.tsx';
import { ITherapeuticIndicationAdmin } from '../../../global/entities/TherapeuticIndication/TherapeuticIndication.ts';

const TherapeuticIndicationResourcesTableInner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    setSorting,
    sortBy,
    reverseSortDirection,
    sortedData,
    setSortedData,
  } = useTable();

  const { data, isLoading } = useGetTherapeuticIndicationsForAdmin();
  const updateResourceUrl = useUpdateTherapeuticIndicationResourceUrl();

  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ITherapeuticIndicationAdmin | null>(null);
  const [formUrlEn, setFormUrlEn] = useState('');
  const [formUrlFr, setFormUrlFr] = useState('');

  useEffect(() => {
    if (!data) return;
    let filtered = data;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.nameEn.toLowerCase().includes(searchLower) ||
          item.nameFr.toLowerCase().includes(searchLower) ||
          (item.resourceUrlEn || '').toLowerCase().includes(searchLower) ||
          (item.resourceUrlFr || '').toLowerCase().includes(searchLower),
      );
    }
    setSortedData(filtered);
  }, [data, search, setSortedData]);

  const openEditModal = (item: ITherapeuticIndicationAdmin) => {
    setEditingItem(item);
    setFormUrlEn(item.resourceUrlEn || '');
    setFormUrlFr(item.resourceUrlFr || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormUrlEn('');
    setFormUrlFr('');
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await updateResourceUrl.mutateAsync({
        id: editingItem.id,
        data: { resourceUrlEn: formUrlEn, resourceUrlFr: formUrlFr },
      });
      notifications.show({
        title: t('therapeuticIndicationResources.title'),
        message: t('therapeuticIndicationResources.saveSuccess'),
        color: 'green',
      });
      closeModal();
    } catch {
      notifications.show({
        title: t('therapeuticIndicationResources.title'),
        message: t('therapeuticIndicationResources.saveError'),
        color: 'red',
      });
    }
  };

  const getDisplayName = (item: ITherapeuticIndicationAdmin) => {
    return i18n.language === 'fr' ? item.nameFr : item.nameEn;
  };

  if (isLoading) return <Loading visible={true} loadingMessage="" />;

  const rows = sortedData.map((item: ITherapeuticIndicationAdmin) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        <Text size="sm" fw={500}>{getDisplayName(item)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" truncate="end" maw={300}>{item.resourceUrlEn || '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" truncate="end" maw={300}>{item.resourceUrlFr || '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={item.isActive ? 'green' : 'gray'} size="sm">
          {item.isActive ? t('therapeuticIndicationResources.active') : t('therapeuticIndicationResources.inactive')}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Tooltip label={t('therapeuticIndicationResources.editResourceUrls')}>
          <ActionIcon
            size="sm"
            variant="subtle"
            color="blue"
            onClick={() => openEditModal(item)}
          >
            <IconEdit size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Flex mb={'xl'} mt={'xl'} align={'center'} justify={'space-between'}>
        <Button variant="transparent">
          <Title c="black" order={4}>
            <IconLink size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {t('therapeuticIndicationResources.title')}
          </Title>
        </Button>
      </Flex>

      <Paper w={'100%'}>
        <Stack gap={'md'}>
          <Flex gap={'md'} align={'center'}>
            <TextInput
              placeholder={t('therapeuticIndicationResources.searchPlaceholder')}
              rightSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
          </Flex>

          <ScrollArea>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Th
                    sorted={sortBy === 'nameEn'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('nameEn', sortedData)}
                  >
                    {t('therapeuticIndicationResources.name')}
                  </Th>
                  <Table.Th>{t('therapeuticIndicationResources.resourceUrlEn')}</Table.Th>
                  <Table.Th>{t('therapeuticIndicationResources.resourceUrlFr')}</Table.Th>
                  <Th
                    sorted={sortBy === 'isActive'}
                    reversed={reverseSortDirection}
                    onSort={() => setSorting('isActive', sortedData)}
                  >
                    {t('therapeuticIndicationResources.status')}
                  </Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </ScrollArea>

          {sortedData.length === 0 && (
            <Text ta="center" c="dimmed" py={'xl'}>
              {t('therapeuticIndicationResources.noData')}
            </Text>
          )}
        </Stack>
      </Paper>

      <Modal
        opened={isModalOpen}
        onClose={closeModal}
        title={t('therapeuticIndicationResources.editResourceUrls')}
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" fw={500}>
            {editingItem ? getDisplayName(editingItem) : ''}
          </Text>

          <TextInput
            label={t('therapeuticIndicationResources.resourceUrlEn')}
            value={formUrlEn}
            onChange={(e) => setFormUrlEn(e.currentTarget.value)}
            placeholder="https://..."
          />

          <TextInput
            label={t('therapeuticIndicationResources.resourceUrlFr')}
            value={formUrlFr}
            onChange={(e) => setFormUrlFr(e.currentTarget.value)}
            placeholder="https://..."
          />

          <Flex justify="flex-end" gap="md" mt="md">
            <Button variant="outline" color="gray" onClick={closeModal}>
              {t('button.cancel')}
            </Button>
            <Button onClick={handleSave} loading={updateResourceUrl.isPending}>
              {t('button.save')}
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};

export const TherapeuticIndicationResourcesTable: React.FC = () => {
  return (
    <TableContextProvider>
      <TherapeuticIndicationResourcesTableInner />
    </TableContextProvider>
  );
};
